import flag from './extensions/flag.json';
import investigateNewWorkbench from './extensions/investigateNewWorkbench.json';
import investigateSourceWorkbench from './extensions/investigateSourceWorkbench.json';
import labels from './extensions/labels.json';
import notes from './extensions/notes.json';
import reviewed from './extensions/reviewed.json';
import editManualEvent from './extensions/editManualEvent.json';
import suppression from './extensions/suppression.json';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { VantageNames } from '@/vantage/constants';
import { ColumnDefinitionInputV1, ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionInputV1';
import { sqItemsApi, sqTableDefinitionsApi } from '@/sdk';
import { ExtensionSchema } from '@/vantage/types/Extension.types';
import { AnyProperty } from '@/utilities.types';
import { TableTypeEnum } from '@/sdk/model/TableDefinitionInputV1';
import { upgradeExtensions } from '@/vantage/extensionsUpgrader';
import { randomUUID } from '@/utilities/utilities';
import { isVantageNameBasedAssetContextEnabled } from '@/services/systemConfiguration.utilities';
import { safeGetJsonProperty } from '@/utilities/httpHelpers.utilities';
import { getExtensions } from '@/vantage/utilities';
import { ColumnRuleInputV1 } from '@/sdk/model/models';

export const bundledExtensions = [
  flag,
  investigateNewWorkbench,
  investigateSourceWorkbench,
  labels,
  notes,
  reviewed,
  suppression,
  editManualEvent,
] as ExtensionSchema[];

export const ALWAYS_INCLUDED_EXTENSIONS = [
  VantageNames.Extensions.Flag,
  VantageNames.Extensions.Reviewed,
  VantageNames.Extensions.Labels,
  VantageNames.Extensions.Suppression,
  VantageNames.Extensions.EditManualEvent,
];

/** Daily */
export const DEFAULT_CRON_SCHEDULE = ['0 0 0 ? * 1,2,3,4,5,6,7'];

export function getExtensionConfigs(extensions: ExtensionSchema[]) {
  let install: ExtensionSchema[] = [];
  let enabled = extensions.filter(({ enabled }) => enabled);

  if (!extensions.length) {
    install = [...bundledExtensions];
    enabled = [...bundledExtensions].map((ext) => ({ ...ext, enabled: true } as ExtensionSchema));
    return { install, enabled };
  }

  if (!enabled.some((ext) => ext.name === VantageNames.Extensions.Notes)) {
    install.push(notes as ExtensionSchema);
    enabled.push(notes as ExtensionSchema);
  }

  for (const extensionName of ALWAYS_INCLUDED_EXTENSIONS) {
    if (!extensions.some((ext) => ext.name === extensionName)) {
      const extension = bundledExtensions.find((ext) => ext.name === extensionName);
      install.push({ ...extension, enabled: false } as ExtensionSchema);
    }
  }

  return { install, enabled: enabled.map(upgradeExtensions) };
}

const column = (
  name: string,
  columnType: ColumnTypeEnum,
  property: string | null | undefined = undefined,
  options: AnyProperty | undefined = undefined,
) => {
  const propertyName = property || name;
  const rules: ColumnRuleInputV1[] = [{ eventProperty: { propertyName } }];
  if (options?.itemColumnIndex) {
    rules.push({ getItemProperty: { propertyName, columnIndex: options.itemColumnIndex } });
  }
  if (options?.usePreserveExistingFallback) {
    rules.push({ preserveExisting: {} });
  }

  return { columnName: name, columnType, columnUom: options?.uom, columnRules: rules };
};

const itemPropertyColumn = (name: string, columnType: ColumnTypeEnum, property: string, options: AnyProperty) => ({
  columnName: name,
  columnType,
  columnUom: options?.uom,
  columnRules: [{ getItemProperty: { propertyName: property, columnIndex: options?.itemColumnIndex } }],
});

const ancestorColumn = (name: string, columnType: ColumnTypeEnum, options: AnyProperty) => ({
  columnName: name,
  columnType,
  columnRules: [{ ancestor: { level: options?.level, columnIndex: options?.columnIndex } }],
});

const pathColumn = (name: string, columnType: ColumnTypeEnum, options: AnyProperty) => ({
  columnName: name,
  columnType,
  columnRules: [{ path: { level: options?.level, columnIndex: options?.columnIndex, separator: ' >> ' } }],
});

const createTable = async (name: string, columns: ColumnDefinitionInputV1[], options: AnyProperty) =>
  sqTableDefinitionsApi
    .createTableDefinition({
      name,
      columnDefinitions: columns,
      tableType: TableTypeEnum.VANTAGE,
      ...options,
    })
    .then(({ data }) => data);

const createEvidenceTable = (groupId: string, subscriptionId: string | null, scopedTo: string) =>
  createTable(
    `Evidence ${groupId}`,
    [
      column(SeeqNames.MaterializedTables.ItemIdColumn, ColumnTypeEnum.UUID, 'Condition ID'),
      column(VantageNames.Columns.DatumID, ColumnTypeEnum.TEXT, SeeqNames.MaterializedTables.DatumIdColumn),
      column(VantageNames.Columns.Start, ColumnTypeEnum.NUMERIC, null, {
        uom: 'ns',
        usePreserveExistingFallback: true,
      }),
      column(VantageNames.Columns.End, ColumnTypeEnum.NUMERIC, null, { uom: 'ns', usePreserveExistingFallback: true }),
      column(VantageNames.Columns.Duration, ColumnTypeEnum.NUMERIC, null, {
        uom: 'ns',
        usePreserveExistingFallback: true,
      }),
      ancestorColumn(VantageNames.Columns.AssetUUID, ColumnTypeEnum.UUID, { level: 1, columnIndex: 1 }),
      itemPropertyColumn(VantageNames.Columns.Asset, ColumnTypeEnum.TEXT, 'Name', { itemColumnIndex: 6 }),
      column(VantageNames.Columns.IsUncertain, ColumnTypeEnum.BOOLEAN),
      column(VantageNames.Columns.LastCapsuleState, ColumnTypeEnum.TEXT, SeeqNames.Properties.Type),
      pathColumn(VantageNames.Columns.Path, ColumnTypeEnum.TEXT, { level: 10, columnIndex: 1 }),
      column(VantageNames.Columns.UpdatedAt, ColumnTypeEnum.TIMESTAMPTZ),
      column(VantageNames.Columns.DeletedAt, ColumnTypeEnum.NUMERIC),
      itemPropertyColumn(VantageNames.Columns.Condition, ColumnTypeEnum.TEXT, 'Name', { itemColumnIndex: 1 }),
      column(VantageNames.Columns.Kind, ColumnTypeEnum.TEXT),
      column(VantageNames.Columns.Mutable, ColumnTypeEnum.BOOLEAN),
      column(VantageNames.Columns.Properties, ColumnTypeEnum.TEXT), // JSON
      column(VantageNames.Columns.Suppressed, ColumnTypeEnum.BOOLEAN, 'Is Suppressed'),
    ],
    { subscriptionId, scopedTo },
  );

export async function installExtensions(workbookId: string, extensions: ExtensionSchema[]) {
  const installedExtensions = await getExtensions(workbookId);
  await sqItemsApi.setProperties(
    [{ name: 'extensions', value: JSON.stringify(installedExtensions.concat(extensions)) }],
    {
      id: workbookId,
    },
  );
}

export const tableConfig = () => ({
  purviewFields: [VantageNames.Columns.Condition, VantageNames.Columns.Asset],
  uniquenessMetaContextMappings: {
    Condition: VantageNames.Columns.ConditionID,
    Asset: VantageNames.Columns.AssetUUID,
  },
  defaultDateRange: {
    lookback: '2 days',
    lookahead: '14 days',
    clockOverride: '3:00:00',
  },
  contextItemIdMapping: {
    Asset: VantageNames.Columns.AssetUUID,
  },
});

export async function initializeVantage(workbookId: string) {
  const groupId = randomUUID();
  const isManualEventsEnabled = await safeGetJsonProperty(workbookId, 'isManualEventsEnabled', false);
  const evidenceTable = await createEvidenceTable(groupId, null, workbookId);
  const properties = {
    extensions: bundledExtensions,
    tableConfig: tableConfig(),
    tableIds: { evidence: evidenceTable.id },
    includeExtinct: false,
  };
  await sqItemsApi.setProperties(
    [
      { name: 'extensions', value: JSON.stringify(properties.extensions) },
      { name: 'includeExtinct', value: JSON.stringify(properties.includeExtinct) },
      { name: 'tableConfig', value: JSON.stringify(properties.tableConfig) },
      { name: 'tableIds', value: JSON.stringify(properties.tableIds) },
    ],
    {
      id: workbookId,
    },
  );
  return {
    workbookId,
    ...properties,
    nameBasedAssetContextOverride: isVantageNameBasedAssetContextEnabled() as unknown as boolean,
    isManualEventsEnabled,
  };
}
